import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BarChart3, TrendingUp, Target, Brain, AlertTriangle, 
  Trophy, Clock, CheckCircle, XCircle, Loader2, ArrowLeft,
  Zap, BookOpen, Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

const scoreColor = (score) => {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-cyan-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};

const ScoreBar = ({ label, score, color = 'bg-purple-500' }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-sm">
      <span className="text-[var(--isp-body)]">{label}</span>
      <span className="font-semibold text-[var(--isp-heading)]">{score}%</span>
    </div>
    <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${score}%` }} />
    </div>
  </div>
);

const GradeChip = ({ grade, count }) => {
  const colors = {
    'A': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'B': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'C': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'D': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'F': 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <div className={`px-3 py-2 rounded-xl border text-center ${colors[grade] || 'bg-white/5 text-white/60 border-white/10'}`}>
      <div className="text-lg font-bold">{grade}</div>
      <div className="text-[10px] opacity-70">{count}x</div>
    </div>
  );
};

const ExamAnalyticsPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const { data } = await api.get(apiUrl('/exam-mode/analytics'));
        setData(data);
      } catch {
        toast.error('Error loading analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!data?.has_data) {
    return (
      <div className="space-y-6" data-testid="exam-analytics-empty">
        <div className="flex items-center gap-3">
          <Link to="/exam-mode">
            <Button aria-label="Back" variant="ghost" size="icon" className="rounded-xl text-[var(--isp-body)]">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-[var(--isp-heading)]">Exam Analytics</h1>
        </div>
        <Card className="p-8 text-center bg-[var(--isp-glass)] border-[var(--isp-glass-border)] ">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] mb-2">No Exam Data Yet</h2>
          <p className="text-[var(--isp-body)] mb-4">Complete practice exams to see your performance analytics here.</p>
          <Link to="/exam-mode">
            <Button className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">Start an Exam</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const { overview, trend, subject_performance, weak_topics, difficulty_performance, recommendations } = data;

  return (
    <div className="space-y-6" data-testid="exam-analytics-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/exam-mode">
          <Button aria-label="Back" variant="ghost" size="icon" className="rounded-xl text-[var(--isp-body)]">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)]">Exam Analytics</h1>
          <p className="text-sm text-[var(--isp-muted)]">Track your performance and find areas to improve</p>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-overview">
        {[
          { label: 'Total Exams', value: overview.total_exams, icon: BookOpen, color: 'text-purple-400' },
          { label: 'Avg Score', value: `${overview.avg_score}%`, icon: Target, color: 'text-cyan-400' },
          { label: 'Questions', value: overview.total_questions, icon: CheckCircle, color: 'text-emerald-400' },
          { label: 'Study Time', value: `${overview.total_time_minutes}m`, icon: Clock, color: 'text-amber-400' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="p-4 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] ">
              <Icon className={`w-5 h-5 ${stat.color} mb-2`} />
              <div className="text-xl sm:text-2xl font-bold text-[var(--isp-heading)]">{stat.value}</div>
              <div className="text-xs text-[var(--isp-muted)]">{stat.label}</div>
            </Card>
          );
        })}
      </div>

      {/* Grade Distribution */}
      {overview.grade_distribution && Object.keys(overview.grade_distribution).length > 0 && (
        <Card className="p-5 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] " data-testid="grade-distribution">
          <h2 className="text-base font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" /> Grade Distribution
          </h2>
          <div className="flex gap-2 flex-wrap">
            {['A', 'B', 'C', 'D', 'F'].map(g => 
              overview.grade_distribution[g] ? <GradeChip key={g} grade={g} count={overview.grade_distribution[g]} /> : null
            )}
          </div>
        </Card>
      )}

      {/* Performance Trend */}
      {trend.length > 1 && (
        <Card className="p-5 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] " data-testid="performance-trend">
          <h2 className="text-base font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" /> Performance Trend
          </h2>
          <div className="flex items-end gap-1 h-32 overflow-x-auto pb-2">
            {trend.map((t, i) => (
              <div key={`trend-${i}`} className="flex flex-col items-center gap-1 min-w-[28px]" title={`${t.subject}: ${t.score}% (${t.grade})`}>
                <span className="text-[9px] text-[var(--isp-muted)]">{t.score}%</span>
                <div
                  className={`w-5 sm:w-6 rounded-t-md transition-all ${scoreColor(t.score)}`}
                  style={{ height: `${Math.max(t.score, 4)}%` }}
                />
                <span className="text-[8px] text-[var(--isp-muted)] truncate max-w-[28px]">{t.grade}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[var(--isp-muted)] mt-2">Oldest → Most Recent</p>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Subject Performance */}
        {subject_performance.length > 0 && (
          <Card className="p-5 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] " data-testid="subject-performance">
            <h2 className="text-base font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-400" /> By Subject
            </h2>
            <div className="space-y-3">
              {subject_performance.map((sp) => (
                <ScoreBar
                  key={sp.subject}
                  label={`${sp.subject} (${sp.exams} exams)`}
                  score={sp.score}
                  color={scoreColor(sp.score)}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Difficulty Breakdown */}
        {difficulty_performance.some(d => d.total > 0) && (
          <Card className="p-5 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] " data-testid="difficulty-breakdown">
            <h2 className="text-base font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> By Difficulty
            </h2>
            <div className="space-y-3">
              {difficulty_performance.filter(d => d.total > 0).map((dp) => (
                <ScoreBar
                  key={dp.difficulty}
                  label={`${dp.difficulty.charAt(0).toUpperCase() + dp.difficulty.slice(1)} (${dp.total} Qs)`}
                  score={dp.score}
                  color={dp.difficulty === 'easy' ? 'bg-emerald-500' : dp.difficulty === 'medium' ? 'bg-cyan-500' : 'bg-rose-500'}
                />
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Weak Topics */}
      {weak_topics.length > 0 && (
        <Card className="p-5 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] " data-testid="weak-topics">
          <h2 className="text-base font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400" /> Weak Topics
          </h2>
          <div className="space-y-2">
            {weak_topics.slice(0, 6).map((wt, i) => (
              <div key={`subj-${i}`} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                  <span className="text-sm text-[var(--isp-body)]">{wt.topic}</span>
                </div>
                <span className="text-xs font-medium text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full">{wt.errors} missed</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* AI Recommendations */}
      <Card className="p-5 bg-[var(--isp-glass)] border-[var(--isp-glass-border)] " data-testid="ai-recommendations">
        <h2 className="text-base font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-400" /> AI Recommendations
        </h2>
        <div className="space-y-3">
          {recommendations.map((rec, i) => (
            <div key={`rec-${i}`} className={`p-4 rounded-xl border ${
              rec.priority === 'high' ? 'bg-rose-500/5 border-rose-500/20' :
              rec.priority === 'medium' ? 'bg-amber-500/5 border-amber-500/20' :
              'bg-emerald-500/5 border-emerald-500/20'
            }`}>
              <div className="flex items-start gap-3">
                <Star className={`w-4 h-4 mt-0.5 shrink-0 ${
                  rec.priority === 'high' ? 'text-rose-400' : rec.priority === 'medium' ? 'text-amber-400' : 'text-emerald-400'
                }`} />
                <div>
                  <p className="text-sm font-medium text-[var(--isp-heading)]">{rec.title}</p>
                  <p className="text-xs text-[var(--isp-body)] mt-0.5">{rec.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Quick Action */}
      <div className="flex gap-3">
        <Link to="/exam-mode" className="flex-1">
          <Button className="w-full rounded-xl gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600" data-testid="practice-exam-btn">
            <Target className="w-4 h-4" /> Practice Exam
          </Button>
        </Link>
        <Link to="/study-buddy" className="flex-1">
          <Button variant="outline" className="w-full rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="study-buddy-btn">
            <Brain className="w-4 h-4" /> Study Buddy
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default ExamAnalyticsPage;
